import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { Reflector } from 'three/examples/jsm/objects/Reflector';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass';

// Ported from the V4 coming-soon site (skweb/static/v4/index.html): the SIKI
// robot GLB with a live canvas face, mirror floor, glow rings, particles and
// bloom, rendered as a full-size backdrop inside its parent element. Skips
// entirely below 1024px or when WebGL fails, leaving the flat hero in place.

const FLOOR_Y = -1.02;
const HOVER = 0.3;
const INTRO_MS = 2400;
const MODEL_URL = '/v4/robot.glb';
const EYES_URL = '/v4/eyes-open.png';

function RobotStage({ wordmark = 'SIKI' }) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || window.innerWidth < 1024) return undefined;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canvas = document.createElement('canvas');
    canvas.className = 'robot-stage__canvas';
    host.appendChild(canvas);

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    } catch (err) {
      console.warn('RobotStage: WebGL unavailable, keeping flat hero', err);
      host.removeChild(canvas);
      return undefined;
    }

    let disposed = false;
    let rafId = null;
    let blinkTimer = null;
    let introStart = null;

    const sizeOf = () => ({
      w: Math.max(1, host.clientWidth),
      h: Math.max(1, host.clientHeight),
    });

    let { w, h } = sizeOf();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();

    // dark studio backdrop, fading to the page background at the edges
    const bgCanvas = document.createElement('canvas');
    bgCanvas.width = bgCanvas.height = 1024;
    const bx = bgCanvas.getContext('2d');
    const bg = bx.createRadialGradient(512, 360, 60, 512, 512, 760);
    bg.addColorStop(0, '#14111A');
    bg.addColorStop(0.45, '#0C0B11');
    bg.addColorStop(1, '#0A0A0F');
    bx.fillStyle = bg;
    bx.fillRect(0, 0, 1024, 1024);
    const bgTex = new THREE.CanvasTexture(bgCanvas);
    bgTex.colorSpace = THREE.SRGBColorSpace;
    scene.background = bgTex;
    scene.fog = new THREE.FogExp2(0x0a0a0f, 0.022);

    // the 38° FOV is vertical, so narrow stages see a thinner slice — back the
    // camera off until the robot and rings fit; wide desktop stays at z = 7
    const fitZ = () => {
      const aspect = w / h;
      const halfVFov = (38 * Math.PI) / 360;
      return Math.min(11.5, Math.max(7.0, 1.9 / (Math.tan(halfVFov) * aspect)));
    };
    const CAM_START = new THREE.Vector3(0, 1.35, Math.max(11.5, fitZ() + 2.5));
    const CAM_END = new THREE.Vector3(0, 0.32, fitZ());
    const LOOK_AT = new THREE.Vector3(0, 0.1, 0);
    const camera = new THREE.PerspectiveCamera(38, w / h, 0.1, 100);
    camera.position.copy(prefersReduced ? CAM_END : CAM_START);

    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    scene.add(new THREE.AmbientLight(0x1a1a22, 0.6));
    const key = new THREE.DirectionalLight(0xffe6b0, 3.0);
    key.position.set(4, 6, 5);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x35e0c9, 3.2);
    rim.position.set(-6, 2, -4);
    scene.add(rim);
    const fill = new THREE.DirectionalLight(0x8a6bff, 1.4);
    fill.position.set(-5, -2, 4);
    scene.add(fill);
    const glow = new THREE.PointLight(0xffee20, 6, 12, 2);
    glow.position.set(0, 0.5, 3);
    scene.add(glow);
    const orbiter = new THREE.PointLight(0xffee20, 4, 10, 2);
    scene.add(orbiter);

    // ghost wordmark behind the robot (reads in the mirror)
    const drawWordmark = () => {
      const gc = document.createElement('canvas');
      gc.width = 2048;
      gc.height = 1024;
      const gx = gc.getContext('2d');
      const fontSize = wordmark.length <= 4 ? 600 : Math.floor(2400 / wordmark.length);
      gx.font = `400 ${fontSize}px "Chakra Petch", sans-serif`;
      gx.textAlign = 'center';
      gx.textBaseline = 'middle';
      gx.lineWidth = 5;
      gx.strokeStyle = 'rgba(255,238,32,0.55)';
      gx.strokeText(wordmark, 1024, 512);
      return new THREE.CanvasTexture(gc);
    };
    const ghostPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(17, 8.5),
      new THREE.MeshBasicMaterial({ map: drawWordmark(), transparent: true, opacity: 0.3, depthWrite: false })
    );
    ghostPlane.position.set(0, 0.7, -5.5);
    scene.add(ghostPlane);
    document.fonts.load('400 600px "Chakra Petch"').then(() => {
      if (disposed) return;
      ghostPlane.material.map.dispose();
      ghostPlane.material.map = drawWordmark();
    }).catch(() => {});

    // mirror floor with a radial fade so it melts into the dark
    const mirror = new Reflector(new THREE.CircleGeometry(14, 64), {
      clipBias: 0.003,
      textureWidth: 1024,
      textureHeight: 1024,
      color: 0x2a2a2a,
    });
    mirror.rotation.x = -Math.PI / 2;
    mirror.position.y = FLOOR_Y;
    scene.add(mirror);

    const fadeC = document.createElement('canvas');
    fadeC.width = fadeC.height = 512;
    const fx = fadeC.getContext('2d');
    const fg = fx.createRadialGradient(256, 256, 40, 256, 256, 256);
    fg.addColorStop(0, 'rgba(10,10,15,0.30)');
    fg.addColorStop(0.55, 'rgba(10,10,15,0.78)');
    fg.addColorStop(1, 'rgba(10,10,15,1)');
    fx.fillStyle = fg;
    fx.fillRect(0, 0, 512, 512);
    const fade = new THREE.Mesh(
      new THREE.CircleGeometry(14.2, 64).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(fadeC), transparent: true, depthWrite: false })
    );
    fade.position.y = FLOOR_Y + 0.006;
    scene.add(fade);

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(1.05, 1.16, 96).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0xffee20, transparent: true, opacity: 0.5, side: THREE.DoubleSide, depthWrite: false })
    );
    ring.position.y = FLOOR_Y + 0.012;
    scene.add(ring);

    const ringOuter = new THREE.Mesh(
      new THREE.RingGeometry(1.55, 1.578, 96).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0xffee20, transparent: true, opacity: 0.12, side: THREE.DoubleSide, depthWrite: false })
    );
    ringOuter.position.y = FLOOR_Y + 0.012;
    scene.add(ringOuter);

    // floating particle field
    const P = 700;
    const pos = new Float32Array(P * 3);
    const col = new Float32Array(P * 3);
    const cGold = new THREE.Color(0xffee20);
    const cCyan = new THREE.Color(0x35e0c9);
    for (let i = 0; i < P; i++) {
      const r = 4 + Math.random() * 10;
      const t = Math.random() * Math.PI * 2;
      const p = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = r * Math.sin(p) * Math.cos(t);
      pos[i * 3 + 1] = Math.random() * 11 - 0.9;
      pos[i * 3 + 2] = r * Math.sin(p) * Math.sin(t);
      const c = i % 3 === 0 ? cCyan : cGold;
      col[i * 3] = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }
    const pg = new THREE.BufferGeometry();
    pg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    pg.setAttribute('color', new THREE.BufferAttribute(col, 3));
    const particles = new THREE.Points(pg, new THREE.PointsMaterial({
      size: 0.03,
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }));
    scene.add(particles);

    // living face: blinking eyes + idle waveform drawn on a canvas mapped onto
    // the model's GlassScreen material with its original texture transform
    let faceCtx = null;
    let faceTexture = null;
    let eyesImage = null;
    let isBlinking = false;
    const faceBars = [];
    const barPatterns = [0.6, 0.4, 0.9, 0.5, 0.7];
    for (let i = 0; i < 5; i++) faceBars.push({ h: 0.2, v: 0 });

    const buildFaceMaterial = () => {
      const fc = document.createElement('canvas');
      fc.width = fc.height = 512;
      faceCtx = fc.getContext('2d');
      faceTexture = new THREE.CanvasTexture(fc);
      faceTexture.magFilter = THREE.NearestFilter;
      faceTexture.minFilter = THREE.NearestFilter;
      faceTexture.colorSpace = THREE.SRGBColorSpace;
      faceTexture.center.set(0.5, 0.5);
      faceTexture.rotation = -Math.PI / 2;
      faceTexture.repeat.set(1.2, 1.5);
      faceTexture.offset.set(0.05, 0.15);
      new THREE.TextureLoader().load(EYES_URL, (tex) => { eyesImage = tex.image; });
      return new THREE.MeshBasicMaterial({ map: faceTexture, side: THREE.DoubleSide });
    };

    const updateFace = (t) => {
      if (!faceCtx || !eyesImage) return;
      const c = faceCtx;
      const W = 512;
      const H = 512;
      c.fillStyle = '#000';
      c.fillRect(0, 0, W, H);

      if (isBlinking) {
        c.strokeStyle = '#FFD700';
        c.lineWidth = 8;
        c.lineCap = 'round';
        const eyeY = H * 0.55;
        const eyeW = 60;
        const spacing = 100;
        c.beginPath(); c.moveTo(W / 2 - spacing, eyeY); c.lineTo(W / 2 - spacing + eyeW, eyeY); c.stroke();
        c.beginPath(); c.moveTo(W / 2 + spacing - eyeW, eyeY); c.lineTo(W / 2 + spacing, eyeY); c.stroke();
      } else {
        c.drawImage(eyesImage, 0, H * 0.25, W, H * 0.75);
      }

      const barW = 25;
      const gap = 15;
      const count = 5;
      const startX = (W - (count * barW + (count - 1) * gap)) / 2 + 30;
      const barY = H * 0.08;
      const maxH = H * 0.12;
      faceBars.forEach((bar, i) => {
        const target = barPatterns[i] * (0.28 + 0.34 * Math.pow(Math.abs(Math.sin(t * 1.9 + i * 0.85)), 2.2));
        bar.v += (target - bar.h) * 0.1;
        bar.v *= 0.7;
        bar.h += bar.v;
        const bh = bar.h * maxH;
        c.shadowBlur = 30;
        c.shadowColor = 'rgba(255,215,0,0.6)';
        c.fillStyle = '#FFD700';
        c.fillRect(startX + i * (barW + gap), barY + (maxH - bh) / 2, barW, bh);
        c.shadowBlur = 0;
      });

      faceTexture.needsUpdate = true;
    };

    const scheduleBlink = () => {
      blinkTimer = setTimeout(() => {
        isBlinking = true;
        setTimeout(() => { isBlinking = false; }, 150);
        scheduleBlink();
      }, 3000 + Math.random() * 3000);
    };
    scheduleBlink();

    // the robot
    const pivot = new THREE.Group();
    scene.add(pivot);
    let modelReady = false;

    const revealScene = () => {
      if (disposed || introStart !== null) return;
      introStart = performance.now();
      canvas.classList.add('ready');
    };

    new GLTFLoader().load(
      MODEL_URL,
      (gltf) => {
        if (disposed) return;
        const model = gltf.scene;
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const scale = 1.8 / Math.max(size.x, size.y, size.z);
        model.scale.setScalar(scale);
        model.position.sub(center.multiplyScalar(scale));
        // The model is already rotated in Blender to face forward — do NOT rotate it.

        const scaledBox = new THREE.Box3().setFromObject(model);
        model.position.y += (FLOOR_Y + HOVER) - scaledBox.min.y;

        const faceMat = buildFaceMaterial();
        model.traverse((o) => {
          if (!o.isMesh || !o.material) return;
          const mats = Array.isArray(o.material) ? o.material : [o.material];
          const out = mats.map((m) => {
            if (m && /glassscreen/i.test(m.name || '')) return faceMat;
            if (m) m.envMapIntensity = 1.25;
            return m;
          });
          o.material = Array.isArray(o.material) ? out : out[0];
        });

        pivot.add(model);
        modelReady = true;
        revealScene();
      },
      undefined,
      () => revealScene() // model error → still show particles/rings
    );
    const revealFallback = setTimeout(() => { if (!modelReady) revealScene(); }, 9000);

    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloom = new UnrealBloomPass(new THREE.Vector2(w, h), 0.55, 0.28, 0.8);
    composer.addPass(bloom);
    composer.addPass(new OutputPass());

    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    const onPointerMove = (ev) => {
      mouse.tx = (ev.clientX / window.innerWidth - 0.5) * 2;
      mouse.ty = (ev.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('pointermove', onPointerMove);

    const onResize = () => {
      ({ w, h } = sizeOf());
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      composer.setSize(w, h);
      CAM_END.z = fitZ();
      CAM_START.z = Math.max(11.5, fitZ() + 2.5);
    };
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(host);

    const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3);
    const clock = new THREE.Clock();

    const animate = () => {
      rafId = requestAnimationFrame(animate);

      // don't burn GPU while the hero is scrolled out of view
      if (window.scrollY > h * 1.2) return;

      const t = clock.getElapsedTime();

      let e = 1;
      if (!prefersReduced && introStart !== null) {
        e = easeOutCubic(Math.min(1, (performance.now() - introStart) / INTRO_MS));
      } else if (!prefersReduced) {
        e = 0; // model still loading — hold the wide shot
      }
      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;

      const targetX = THREE.MathUtils.lerp(CAM_START.x, CAM_END.x, e) + mouse.x * 0.45 * e;
      const targetY = THREE.MathUtils.lerp(CAM_START.y, CAM_END.y, e) - mouse.y * 0.3 * e;
      camera.position.x += (targetX - camera.position.x) * 0.06;
      camera.position.y += (targetY - camera.position.y) * 0.06;
      camera.position.z = THREE.MathUtils.lerp(CAM_START.z, CAM_END.z, e);
      camera.lookAt(LOOK_AT);

      pivot.rotation.y = mouse.x * 0.4 + Math.sin(t * 0.25) * 0.05;
      pivot.rotation.x = Math.sin(t * 0.6) * 0.04 + mouse.y * 0.16;
      pivot.position.y = Math.sin(t * 0.9) * 0.1;

      ring.material.opacity = 0.42 + Math.sin(t * 1.7) * 0.14;
      orbiter.position.set(Math.cos(t * 0.5) * 3.4, 1.6, Math.sin(t * 0.5) * 3.4 + 1);
      particles.rotation.y = t * 0.03;

      updateFace(t);
      composer.render();
    };
    animate();

    return () => {
      disposed = true;
      cancelAnimationFrame(rafId);
      clearTimeout(blinkTimer);
      clearTimeout(revealFallback);
      window.removeEventListener('pointermove', onPointerMove);
      resizeObserver.disconnect();
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        const mats = Array.isArray(o.material) ? o.material : (o.material ? [o.material] : []);
        mats.forEach((m) => {
          if (m.map) m.map.dispose();
          m.dispose();
        });
      });
      composer.dispose();
      pmrem.dispose();
      renderer.dispose();
      if (canvas.parentNode === host) host.removeChild(canvas);
    };
  }, [wordmark]);

  return <div className="robot-stage" ref={hostRef} aria-hidden="true" />;
}

export default RobotStage;
